import { SimpleGrid, Text } from '@chakra-ui/react';
import { InputControl } from '@pluxee-design-system/react-hook-form';
import useConfig from 'data/useConfig';
import { RuianData } from 'generated';
import useTranslations from 'i18n';
import { identity } from 'lodash';
import { ChangeEventHandler, useCallback, useMemo, useRef } from 'react';
import { SetValueConfig, useFormContext } from 'react-hook-form';
import { ConfigField } from 'types/config';
import { getHouseNumber, parseHouseNumbers } from 'utils/getHouseNumber';
import maskString from 'utils/maskString';
import AddressAutocompleteControl from './AddressAutocompleteControl';

interface AddressFieldProps {
  streetName: string;
  houseNumber: string;
  orientationNumber?: string;
  box?: string;
  city: string;
  postalCode: string;
}

interface AddressControlProps {
  config: {
    streetName: ConfigField;
    houseNumber: ConfigField;
    orientationNumber?: ConfigField;
    box?: ConfigField;
    city: ConfigField;
    postalCode: ConfigField;
  };
  disabled?: boolean;
  idPrefix: string;
  readonly?: boolean;
  tPrefix: string;
  withSuggestion?: boolean;
}

const AddressControl = ({
  config,
  disabled,
  idPrefix,
  readonly,
  tPrefix,
  withSuggestion = false,
}: AddressControlProps) => {
  const { t } = useTranslations();
  const { setValue } = useFormContext<AddressFieldProps>();
  const { addressAutocomplete: isAddressSuggestionEnabled } = useConfig();
  const showSuggestion = isAddressSuggestionEnabled && withSuggestion && !readonly;
  const orientationNumberRef = useRef<HTMLInputElement>(null);

  const handleSetAddress = useCallback(
    (op?: RuianData) => {
      const options: SetValueConfig = { shouldTouch: true, shouldDirty: true };
      setValue('streetName', op?.street || op?.city || '', options);
      if (config.houseNumber.visible) {
        const { houseNumber, orientationNumber } = parseHouseNumbers(
          op?.houseNumber,
          op?.houseOrientationNumber,
          config.orientationNumber?.visible,
        );
        setValue('houseNumber', houseNumber, options);

        if (config.orientationNumber?.visible)
          setValue('orientationNumber', orientationNumber, options);
      }
      if (config.city.visible) setValue('city', op?.city || '', options);
      if (config.postalCode.visible)
        setValue('postalCode', maskString(op?.zip || '', config.postalCode.format), options);
    },
    [config, setValue],
  );

  const handleChangeHouseNumber = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      let houseNumber = event.target.value;
      const options: SetValueConfig = { shouldTouch: true, shouldDirty: true, shouldValidate: true };

      if (config.orientationNumber?.visible) {
        let orientationNumber: string, hasSlash: boolean;
        ({ houseNumber, orientationNumber, hasSlash } = getHouseNumber(event.target.value));

        if (hasSlash && orientationNumber)
          setValue('orientationNumber', orientationNumber, options);
        if (hasSlash) orientationNumberRef.current?.focus();
      }

      setValue('houseNumber', houseNumber, options);
    },
    [config.orientationNumber?.visible, setValue],
  );

  const streetColumns = useMemo(
    () =>
      [config.streetName.visible, config.houseNumber.visible, config.box?.visible].filter(identity)
        .length,
    [config.box?.visible, config.houseNumber.visible, config.streetName.visible],
  );

  return (
    <>
      {streetColumns > 0 && (
        <SimpleGrid columns={{ base: 1, md: streetColumns }} spacing={4}>
          {config.streetName.visible &&
            (showSuggestion ? (
              <AddressAutocompleteControl
                readonly={readonly}
                disabled={disabled}
                mandatory={config.streetName.mandatory}
                name="streetName"
                id={`${idPrefix}_streetName`}
                placeholder={t(`${tPrefix}.streetName.label`, 'Street name')}
                title={t(`${tPrefix}.streetName.label`, 'Street name')}
                onClear={handleSetAddress}
                onOptionSelect={handleSetAddress}
                loadingText={t('global_texts.labels.loading_data', 'Loading...')}
                notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
              />
            ) : (
              <InputControl
                readonly={readonly}
                disabled={disabled}
                type="text"
                mandatory={config.streetName.mandatory}
                name="streetName"
                id={`${idPrefix}_streetName`}
                placeholder={t(`${tPrefix}.streetName.label`, 'Street name')}
                title={t(`${tPrefix}.streetName.label`, 'Street name')}
              />
            ))}
          {(config.houseNumber.visible || config.orientationNumber?.visible) && (
            <SimpleGrid
              templateColumns={
                config.orientationNumber?.visible
                  ? 'minmax(0, 1fr) 5px minmax(0, 1fr)'
                  : 'minmax(0, 1fr)'
              }
              spacing={4}
              w="100%"
              sx={{
                '.chakra-input__right-element': { display: 'none' },
                input: { paddingRight: 4 },
              }}
            >
              <InputControl
                readonly={readonly}
                disabled={disabled}
                type="text"
                mandatory={config.houseNumber.mandatory}
                name="houseNumber"
                id={`${idPrefix}_houseNumber`}
                placeholder={t(`${tPrefix}.houseNumber.label`, 'House number')}
                title={t(`${tPrefix}.houseNumber.label`, 'House number')}
                onChange={handleChangeHouseNumber}
              />
              {config.orientationNumber?.visible && (
                <>
                  <Text
                    as="div"
                    pt={6}
                    lineHeight="48px"
                    variant="body.largeXbold"
                    color="semantic.text.quaternary"
                  >
                    /
                  </Text>
                  <InputControl
                    // @ts-expect-error Actually it does forward a ref, but the types are wrong
                    ref={orientationNumberRef}
                    readonly={readonly}
                    disabled={disabled}
                    type="text"
                    mandatory={config.orientationNumber.mandatory}
                    name="orientationNumber"
                    id={`${idPrefix}_orientationNumber`}
                    placeholder={t(`${tPrefix}.orientationNumber.label`, 'Orientation number')}
                    title={t(`${tPrefix}.orientationNumber.label`, 'Orientation number')}
                  />
                </>
              )}
            </SimpleGrid>
          )}
          {config.box?.visible && (
            <InputControl
              readonly={readonly}
              disabled={disabled}
              type="text"
              mandatory={config.box?.mandatory}
              name="box"
              id={`${idPrefix}_box`}
              placeholder={t(`${tPrefix}.box.label`, 'Box')}
              title={t(`${tPrefix}.box.label`, 'Box')}
            />
          )}
        </SimpleGrid>
      )}
      {(config.city.visible || config.postalCode.visible) && (
        <SimpleGrid
          columns={{ base: 1, md: config.city.visible && config.postalCode.visible ? 2 : 1 }}
          spacing={4}
        >
          {config.city.visible && (
            <InputControl
              readonly={readonly}
              disabled={disabled}
              type="text"
              mandatory={config.city.mandatory}
              name="city"
              id={`${idPrefix}_city`}
              placeholder={t(`${tPrefix}.city.label`, 'City')}
              title={t(`${tPrefix}.city.label`, 'City')}
            />
          )}
          {config.postalCode.visible && (
            <InputControl
              readonly={readonly}
              disabled={disabled}
              type="text"
              mandatory={config.postalCode.mandatory}
              name="postalCode"
              id={`${idPrefix}_postalCode`}
              placeholder={t(`${tPrefix}.postalCode.label`, 'ZIP Code')}
              title={t(`${tPrefix}.postalCode.label`, 'ZIP Code')}
            />
          )}
        </SimpleGrid>
      )}
    </>
  );
};

export default AddressControl;
